'use client';

import { c as _c } from "react/compiler-runtime";
import { memo } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import ImageGallery from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/ImageGallery';
import { ResponsiveImageDataWithBadges } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/components/GalleryImage';
import { useResolvedAppearances } from '@/components/ProductCard/AppearanceConfigProvider';
import { SprdResponsiveImageData } from '@/components/ResponsiveImage/SprdResponsiveImage';
import { embroideryProductKeyAtom, pdpGalleryOffsetDurationAtom, showEmbroideryOnDetailPageAtom } from '@/store/atoms';
import { FEATURE_SUSTAINABLE_ID, STICKER_PRODUCT_TYPE_IDS } from '@/utility/constants';
import { Locale, getApiLocale } from '@/utility/locale';
import { getProductTypeModelImagesForView } from '@/utility/productTypeImages';
import { getCompositionImageAltText, getProductTypeImageAltText } from '@/utility/seo';
import { GalleryBadgeLabels, getHeroImageBadges, getOurPickBadge } from '../utility/galleryBadges';
import { isEmbroideryProductType, isNewProductType } from '../utility/label';

/** The only composition fields the gallery reads; the full composition stays on the server. */
export type MpImageGalleryComposition = Pick<Composition, 'name' | 'embroidery' | 'publishingDate'>;
type MpImageGalleryProps = {
  locale: Locale;
  sellable: Sellable;
  appearanceId: string;
  modelImages?: ModelImage[];
  composition: MpImageGalleryComposition;
  productType: ClientProductType;
  spacingFactor?: number;
  isSpreadBrand?: boolean;
  imageAltText?: string;
  isTemplate?: boolean;
};
const MP_TAG = 'mp';
export const MpImageGallery = memo(t0 => {
  const $ = _c(15);
  const {
    locale,
    sellable,
    appearanceId,
    modelImages,
    composition,
    productType,
    spacingFactor,
    isSpreadBrand,
    imageAltText,
    isTemplate
  } = t0;
  const defaultViewId = String(sellable.defaultViewId);
  const defaultViewModelImages = getProductTypeModelImagesForView(modelImages, appearanceId, defaultViewId, "detail", MP_TAG);
  const otherViewIds = [...new Set(modelImages?.reduce((acc, img) => {
    if (img.appearanceIds.includes(appearanceId) && img.viewId !== defaultViewId) {
      acc.push(img.viewId);
    }
    return acc;
  }, []))];
  const otherViewsModelImages = otherViewIds.flatMap(viewId => getProductTypeModelImagesForView(modelImages, appearanceId, viewId, "detail", MP_TAG));
  const allModelImages = [...defaultViewModelImages, ...otherViewsModelImages];
  const t = useTranslations();
  const badgeLabels = {
    sortNew: t("filter.sortNew"),
    sustainable: t("filter.sustainable"),
    embroidery: t("filter.embroidery"),
    ourPick: t("mpDetailPage.ourPickBadge"),
    template: t("mpDetailPage.templateBadge")
  };
  const badgeFlags = {
    isSpreadBrand,
    isNew: isNewProductType(composition),
    isSustainable: productType.labels?.features.some(_temp),
    isEmbroidery: isEmbroideryProductType(composition),
    isTemplate
  };
  let t1;
  if ($[0] !== appearanceId) {
    t1 = [appearanceId];
    $[0] = appearanceId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [appearance] = useResolvedAppearances(t1);
  const color = appearance?.colors[0];
  const usedPrintAreaIds = sellable.printAreas.map(_temp2);
  const viewsWithUsedPrintAreas = productType.views.filter(view => view.viewMaps.some(vm => usedPrintAreaIds.includes(vm.printArea.id))).map(_temp3);
  let t2;
  if ($[2] !== sellable.productTypeId) {
    t2 = STICKER_PRODUCT_TYPE_IDS.includes(String(sellable.productTypeId));
    $[2] = sellable.productTypeId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isSticker = t2;
  const showCompositionImage = usedPrintAreaIds.length > 0;
  const compositionName = composition.name[getApiLocale(locale)] ?? "";
  const embroideryProductKey = useAtomValue(embroideryProductKeyAtom);
  const showEmbroidery = useAtomValue(showEmbroideryOnDetailPageAtom);
  const offsetDuration = useAtomValue(pdpGalleryOffsetDurationAtom);
  const vpString = showEmbroidery && embroideryProductKey ? embroideryProductKey : sellable.vpString;
  const exchangeFirstImage = showCompositionImage && !isSticker && !showEmbroidery;
  const ourPickBadge = getOurPickBadge(badgeLabels.ourPick);
  const showOurPickOnComposition = isSpreadBrand && !exchangeFirstImage && showCompositionImage;
  const showOurPickOnHeroImage = isSpreadBrand && exchangeFirstImage;
  const T0 = ImageGallery;
  const t3 = showCompositionImage ? [{
    type: "composition" as const,
    vpString,
    viewId: String(sellable.defaultViewId),
    appearanceId,
    loading: exchangeFirstImage ? "lazy" : "eager",
    priority: exchangeFirstImage ? "low" : "high",
    alt: isSticker ? getCompositionImageAltText(compositionName, productType.name, appearance?.name ?? "", productType.views.find(v_0 => v_0.id === String(sellable.defaultViewId))?.name) : compositionName,
    backgroundColor: color,
    badges: showOurPickOnComposition ? [ourPickBadge] : undefined
  } satisfies ResponsiveImageDataWithBadges] : [];
  const t4 = !defaultViewModelImages.length ? [{
    type: "product",
    vpString,
    viewId: String(sellable.defaultViewId),
    appearanceId,
    loading: exchangeFirstImage || !showCompositionImage ? "eager" : "lazy",
    priority: exchangeFirstImage || !showCompositionImage ? "high" : "low",
    alt: getCompositionImageAltText(compositionName, productType.name, appearance?.name ?? "", productType.views.find(v_1 => v_1.id === String(sellable.defaultViewId))?.name),
    badges: showOurPickOnHeroImage ? [ourPickBadge] : undefined
  } satisfies ResponsiveImageDataWithBadges] : [];
  const t5 = allModelImages.map((image, index) => {
    const isHeroImage = index === 0 && !!defaultViewModelImages.length && (exchangeFirstImage || !showCompositionImage);
    return viewsWithUsedPrintAreas.includes(image.viewId) ? {
      type: "product",
      vpString,
      viewId: image.viewId,
      appearanceId: image.appearanceId,
      modelId: image.model?.modelId,
      crop: image.crop,
      alt: getCompositionImageAltText(compositionName, productType.name, appearance?.name ?? "", productType.views.find(v_2 => v_2.id === image.viewId)?.name, image.model?.persons),
      badges: index === 0 ? getHeroImageBadges(badgeFlags, badgeLabels, {
        showOurPickOnHeroImage: !!showOurPickOnHeroImage
      }) : undefined,
      loading: isHeroImage ? "eager" : "lazy",
      priority: isHeroImage ? "high" : "low"
    } satisfies ResponsiveImageDataWithBadges : {
      type: "productType",
      productTypeId: productType.id,
      viewId: image.viewId,
      appearanceId: image.appearanceId,
      modelId: image.model?.modelId,
      alt: getProductTypeImageAltText(productType.name, appearance?.name ?? "", productType.views.find(v_3 => v_3.id === image.viewId)?.name, image.model?.persons),
      crop: image.crop
    } satisfies SprdResponsiveImageData;
  });
  let t6;
  if ($[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = [...t3, ...t4, ...t5];
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== T0 || $[9] !== exchangeFirstImage || $[10] !== imageAltText || $[11] !== offsetDuration || $[12] !== spacingFactor || $[13] !== t6) {
    t7 = <T0 exchangeFirstImageOnMobile={exchangeFirstImage} spacingFactor={spacingFactor} bottomOffsetDuration={offsetDuration} imageAltText={imageAltText} images={t6} />;
    $[8] = T0;
    $[9] = exchangeFirstImage;
    $[10] = imageAltText;
    $[11] = offsetDuration;
    $[12] = spacingFactor;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
});
MpImageGallery.displayName = 'MpImageGallery';
function _temp(label) {
  return label.id === FEATURE_SUSTAINABLE_ID;
}
function _temp2(pa) {
  return String(pa.printAreaId);
}
function _temp3(v) {
  return v.id;
}